/**
 * Recover a tab whose route map names a hashed module removed by a newer release (#799).
 *
 * The deploy keeps the immediately previous release's assets as the primary protection. This client fallback is
 * deliberately one-shot per tab: after one worker refresh + hard reload, a repeated error reaches the normal route
 * error boundary instead of entering a reload loop.
 */

export const CHUNK_RECOVERY_SESSION_KEY = 'patchbay:chunk-recovery-attempted'

const CHUNK_ERROR_PATTERNS = [
  /chunkloaderror/i,
  /loading (?:css )?chunk [\w-]+ failed/i,
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /unable to preload css/i,
]

function errorText(value: unknown): string {
  if (value instanceof Error) return `${value.name}: ${value.message}`
  if (typeof value === 'string') return value
  if (value && typeof value === 'object') {
    const candidate = value as { name?: unknown; message?: unknown }
    return `${typeof candidate.name === 'string' ? candidate.name : ''}: ${typeof candidate.message === 'string' ? candidate.message : ''}`
  }
  return ''
}

export function isChunkLoadError(value: unknown): boolean {
  const text = errorText(value)
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(text))
}

interface RecoveryStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

export interface ChunkRecoveryEnvironment {
  storage: RecoveryStorage
  updateServiceWorker(): Promise<void>
  reload(): void
}

function browserEnvironment(): ChunkRecoveryEnvironment {
  return {
    storage: window.sessionStorage,
    updateServiceWorker: async () => {
      if (!('serviceWorker' in navigator)) return
      const registration = await navigator.serviceWorker.getRegistration()
      await registration?.update()
    },
    reload: () => window.location.reload(),
  }
}

/** Claims the one allowed recovery synchronously, then refreshes the worker and reloads even if that refresh fails. */
export function recoverChunkLoad(value: unknown, environment?: ChunkRecoveryEnvironment): boolean {
  if (!isChunkLoadError(value)) return false
  const env = environment ?? browserEnvironment()
  try {
    if (env.storage.getItem(CHUNK_RECOVERY_SESSION_KEY) === '1') return false
    env.storage.setItem(CHUNK_RECOVERY_SESSION_KEY, '1')
  } catch {
    // sessionStorage may be disabled. Without a durable guard, reloading could loop, so show the normal error.
    return false
  }
  void env
    .updateServiceWorker()
    .catch(() => {})
    .finally(env.reload)
  return true
}

type VitePreloadErrorEvent = Event & { payload?: unknown }

/** Install before the router so Vite preload failures can be suppressed only for the single recovery attempt. */
export function installChunkRecovery(target: Pick<Window, 'addEventListener'> = window, environment?: ChunkRecoveryEnvironment): void {
  target.addEventListener('vite:preloadError', ((event: VitePreloadErrorEvent) => {
    // The Vite event itself is authoritative even where a browser's module error text is generic.
    const error = event.payload ?? new Error('Failed to fetch dynamically imported module')
    if (recoverChunkLoad(error, environment)) event.preventDefault()
  }) as EventListener)
  target.addEventListener('unhandledrejection', ((event: PromiseRejectionEvent) => {
    if (recoverChunkLoad(event.reason, environment)) event.preventDefault()
  }) as EventListener)
}
